import React, { useMemo } from "react";
import { useAppSelector } from "../../../store";
import defaultContent from "../../../content/actionReportsWhosNew";
import commonContent from "../../../content/common";
import "../ActionReports.scss";
import { useTranslation } from "react-i18next";
import { FetchWhosNewActionReportData } from "./WhosNewActionReportDataLayer";
import { ActionReportListView, ActionReportListViewLoader } from "../common/ActionReportListView/ActionReportListView";
import { CollapsibleSection } from "../common/CollapsibleSection/CollapsibleSection";
import { getCurrentPeriod, getFullMonth, getPreviousNPeriods } from "../../../common/util/period";
import "./WhosNewActionReport.scss";
import type { RecordRef, WhosNewReportRecord } from "../../../services/ActionReports/ActionReportAPI.types";
import { storedLocale } from "../../Authentication/util";
import { ERROR_TYPE, ErrorPage, IconType } from "../../ErrorPage/ErrorPage";
import { KPI_ID } from "../../../services/AboEnrichment/AboEnrichmentAPI.types";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { useOutletContext } from "react-router-dom";
import { selectGroupLeader } from "../../../reducers/groupLeaderFilter";
import { recordsInLeaderSubtree } from "../common/groupLeaderGrouping";
import { getBusinessNatureTag, isCustomerBusinessNature } from "../utils/businessNature";
import {
  whosNewProfileTrack,
  whosNewAddToWatchlistTrack,
  whosNewPhoneIconTrack,
  whosNewMessageIconTrack,
} from "../../../common/tealium/ActionReports/WhosNewTrack";
import { ErrorTile } from "../../../common/components/ErrorTile/ErrorTile";
import { useGetSavedKpiPreferenceQuery } from "../../../services/KPIPreferences/savedKpiPreferencesAPI";

const DEFAULT_KPIS = ["ppvRequirement", "groupPV", "sponsoring", "totalABOCount", "customers", "autoRenewal"];

const PERIOD_COUNT = 12;

const createFieldConfig = (tableColumns: { [key: string]: string }, ppvTitle: string) => [
  { id: KPI_ID.PERSONAL_PV_REQ, kpi: "ppvRequirement", title: ppvTitle },
  { id: KPI_ID.ANNUAL_PPV, kpi: "annualPPV", title: tableColumns["annualPPV"] },
  { id: KPI_ID.GROUP_PV, kpi: "groupPV", title: tableColumns["gpv"] },
  { id: KPI_ID.GROUP_SIZE, kpi: "totalABOCount", title: tableColumns["groupSize"] },
  { id: KPI_ID.SPONSORING, kpi: "sponsoring", title: tableColumns["sponsoring"] },
  { id: KPI_ID.SPONSORING_YTD, kpi: "sponsoringYtdStar", title: tableColumns["sponsoringYtd"] },
  { id: KPI_ID.CUSTOMER_COUNT, kpi: "customers", title: tableColumns["customers"] },
  { id: KPI_ID.SPONSOR_NAME, kpi: "sponsorName", title: tableColumns["sponsorName"] },
  { id: KPI_ID.AUTO_RENEWAL, kpi: "autoRenewal", title: tableColumns["autoRenewal"] },
  { id: KPI_ID.PERSONAL_PV, kpi: "ppv", title: tableColumns["ppv"] },
  { id: KPI_ID.WHATSAPP, kpi: "whatsapp", title: "" },
];

export const filterKpisByConfiguration = (selectedKpis: string[], configuredKpis: string[] | null | undefined) =>
  configuredKpis === null || configuredKpis === undefined
    ? selectedKpis
    : selectedKpis.filter((kpi) => configuredKpis.includes(kpi));

const useSelectedKpis = () => {
  const { savedKpiPreferencesData } = useAppSelector((state) => state.savedKpiPreferencesData);
  const { configuration } = useAppSelector((state) => state.boot);
  const availableKpis = configuration.uiKpis.whosNewV2;
  useGetSavedKpiPreferenceQuery("whosNewKPIs", { refetchOnMountOrArgChange: true });

  return useMemo(() => {
    const savedKpis = savedKpiPreferencesData?.savedDataViews?.whosNewKPIs?.availableKpis;
    const selectedKpis = savedKpis ? savedKpis.filter((kpi) => kpi.displaydefault).map((kpi) => kpi.id) : DEFAULT_KPIS;
    return filterKpisByConfiguration(selectedKpis, availableKpis);
  }, [savedKpiPreferencesData, availableKpis]);
};

const PeriodTableLoader = ({ count }: { count: number }) => (
  <div className="WhosNewAR__tableWrapper">
    {Array.from({ length: count }).map((_, index) => (
      <React.Fragment key={index}>
        <section className="WhosNewAR__tableTitle">
          <LeafRectangleLoader isLoading height="1.5rem" width="8rem" margin="0" />
        </section>
        <div className="WhosNewAR__listViewWrapper">
          <ActionReportListViewLoader />
        </div>
      </React.Fragment>
    ))}
  </div>
);

export default function WhosNewActionReport() {
  const { t } = useTranslation([
    "actionReportsWhosNew",
    "error",
    "ssiUplineReport",
    "actionReports",
    "leafAboName",
    "common",
  ]);
  const { availableKpis } = useAppSelector((state) => state.whosNewActionReportTracker);
  const currentPeriod = getCurrentPeriod();
  const selectedLeader = useAppSelector(selectGroupLeader);
  const { configuration } = useAppSelector((state) => state.boot);
  const confidentialUser = t("ConfidentialUser", "Confidential User", { ns: "leafAboName" });
  const { whosNewActionReportData, isLoading, errorStatus, refetch } = FetchWhosNewActionReportData(currentPeriod);
  const {
    ppvTitleKey,
    scrollToAbo,
    sortDirection,
    showWhosNewIncludesCustomers = false,
  } = useOutletContext<{
    ppvTitleKey: string;
    sortDirection: "asc" | "desc";
    scrollToAbo?: { aff: string; abo: string; period: string };
    showWhosNewIncludesCustomers: boolean;
  }>();

  const leaders = whosNewActionReportData.GroupLeaders ?? [];
  const tree = whosNewActionReportData.GroupLeaderTree ?? [];

  const tableColumns = t("table.columns", {
    ns: "actionReportsWhosNew",
    returnObjects: true,
    defaultValue: (defaultContent.table as { columns: { [key: string]: string } }).columns,
  }) as { [key: string]: string };

  const ppvTitle = t(ppvTitleKey, commonContent[ppvTitleKey], { ns: "common" });

  const selectedKpis = useSelectedKpis();
  const fieldConfig = useMemo(() => createFieldConfig(tableColumns, ppvTitle), [tableColumns, ppvTitle]);

  const arWrapperList = (affAboList: WhosNewReportRecord[], conversionPeriod: string) => {
    const newCustomerLabel = t("newCustomer", defaultContent["newCustomer"] as string, {
      ns: "actionReportsWhosNew",
    });

    const getWhosNewName = (record: WhosNewReportRecord) => {
      if (record.ConfidentialUser) {
        return confidentialUser;
      }
      if (isCustomerBusinessNature(record.BusinessNature) && record.Level > 2 && !showWhosNewIncludesCustomers) {
        return newCustomerLabel;
      }
      return record.Name;
    };

    // Find the index of the searched ABO within this period's data
    let scrollToIndex: number | undefined = undefined;
    if (scrollToAbo && scrollToAbo.period === conversionPeriod) {
      scrollToIndex = affAboList.findIndex(
        (record) => record.Aff === scrollToAbo.aff && record.Abo === scrollToAbo.abo,
      );
      if (scrollToIndex === -1) {
        scrollToIndex = undefined;
      }
    }

    const reportData = affAboList.map((record) => ({
      aboName: getWhosNewName(record),
      aboNumber: record.Abo,
      abo: record.Abo,
      aff: record.Aff,
      countryCode: record.IsoCountry,
      isProvisional: record.Provisional,
      phoneNumber: record.PhoneNumber,
      isConfidential:
        record.ConfidentialUser ||
        (isCustomerBusinessNature(record.BusinessNature) && record.Level > 2 && !showWhosNewIncludesCustomers),
      isCustomer: isCustomerBusinessNature(record.BusinessNature),
      allowCustomerDetails: showWhosNewIncludesCustomers && !record.ConfidentialUser,
      businessNature: record.BusinessNature,
      businessNatureTag: showWhosNewIncludesCustomers ? getBusinessNatureTag(record.BusinessNature) : undefined,
      level: record.Level,
      imageRefUrl: record.ImageRefURL,
      conversionType: record.ConversionType,
    }));

    return (
      <div className="WhosNewAR__listViewWrapper">
        <ActionReportListView
          selectedKpis={selectedKpis.filter((id) => availableKpis.includes(id))}
          reportId="whosNewAR"
          reportData={reportData}
          period={currentPeriod}
          fieldConfig={fieldConfig}
          enableWatchlist={true}
          scrollToIndex={scrollToIndex}
          aboHeader={configuration.actionReports.showWhosNewIncludesCustomers ? "nameNumber" : "aboNameNumber"}
          showImageBlock={true}
          requestWithSponsorForCustomers={showWhosNewIncludesCustomers === true}
          onProfileTrack={whosNewProfileTrack}
          onWatchlistTrack={(isAdding) => {
            if (isAdding) whosNewAddToWatchlistTrack();
          }}
          onPhoneTrack={whosNewPhoneIconTrack}
          onMessageTrack={whosNewMessageIconTrack}
        />
      </div>
    );
  };

  const getFirstGroupLeader = (r: WhosNewReportRecord): RecordRef | undefined => r.FirstGroupLeader;
  const getSelfRef = (r: WhosNewReportRecord): RecordRef => ({ aff: r.Aff, abo: r.Abo });

  const renderPeriodRangeView = (periodCount: number) => {
    if (isLoading) {
      return <PeriodTableLoader count={periodCount} />;
    }

    if (errorStatus) {
      return (
        <ErrorTile
          errorStatus={errorStatus}
          isLoading={isLoading}
          clickFunction={refetch}
          tealiumTrackId="WhosNew_Action-Report_Error"
        />
      );
    }

    const periodsDesc = getPreviousNPeriods(currentPeriod, periodCount, true);
    const periods = sortDirection === "asc" ? [...periodsDesc].reverse() : periodsDesc;
    const inWindow = whosNewActionReportData.Whosnew.filter((abo) => periods.includes(abo.ConversionPeriod));

    if (inWindow.length === 0) {
      return (
        <div className="WhosNewAR__tableWrapper">
          <div className="WhosNewAR__noDownlines">
            <ErrorPage errorType={ERROR_TYPE.NO_DOWNLINES} iconType={IconType.SearchCriteria} />
          </div>
        </div>
      );
    }

    // Scoped view narrows the records to the selected leader's subtree;
    // the rendered layout is the same month-grouped list as the "All"
    // view — Figma omits the leader-name section header on this report.
    const records =
      selectedLeader && leaders.length > 0
        ? recordsInLeaderSubtree(inWindow, tree, selectedLeader, getSelfRef, getFirstGroupLeader)
        : inWindow;

    if (selectedLeader && leaders.length > 0 && records.length === 0) {
      return (
        <div className="WhosNewAR__tableWrapper">
          <div className="WhosNewAR__noDownlines">
            <ErrorPage errorType={ERROR_TYPE.NO_DOWNLINES} iconType={IconType.SearchCriteria} />
          </div>
        </div>
      );
    }

    return (
      <div className="WhosNewAR__tableWrapper">
        {periods.map((period) => {
          const periodData = records.filter((abo) => abo.ConversionPeriod === period);
          if (periodData.length === 0) return null;
          return (
            <CollapsibleSection
              key={period}
              label={getFullMonth(period, storedLocale())}
              count={periodData.length}
              whosNewScrollToAbo={scrollToAbo}
              scrollIdentifier={period}
              expandKey={selectedLeader}
              enableDesktopCollapse
              data-testid={`whos-new-period-${period}`}
            >
              {arWrapperList(periodData, period)}
            </CollapsibleSection>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div>
        <div className="WhosNewAR__content" data-testid="whos-new-ar-content">
          {renderPeriodRangeView(PERIOD_COUNT)}
        </div>
      </div>
    </>
  );
}
